/*
  Richpanel chat: a name, focus on open, Esc to close. (DTC-154)
  WCAG 2.4.3 focus order, 4.1.2 name, 2.1.2 no keyboard trap.

  The widget is Richpanel's, but its messenger is a same-origin <iframe>
  (no src; Richpanel writes into it), so we can reach in from here:

    - the frame gets a title, so it is not "frame, unnamed";
    - when the chat is opened FROM THE KEYBOARD (Enter or Space on the
      launcher), focus moves to the first control in it. A tap or click
      changes nothing, so phones do not raise the on-screen keyboard;
    - Esc in the open chat presses Richpanel's own "Close chat" button and
      puts focus on its "Open chat" launcher, as a keyboard close would.

  Richpanel swaps the frame's class between richpanel-messenger-opened and
  -closed; after the first open the launcher lives inside the same frame.
*/
(() => {
  const FRAME_ID = 'richpanel_messenger_iframe';
  const TITLE = 'Chat with Nectar support';
  const KEY_WINDOW_MS = 4000;
  let keyboardAt = 0;

  const isActivation = (e) => e.key === 'Enter' || e.key === ' ';
  const findButton = (doc, re) => [...doc.querySelectorAll('button, [role="button"]')]
    .find((el) => re.test(el.getAttribute('aria-label') || '') && el.getClientRects().length);
  const firstFocusable = (doc) => [...doc.querySelectorAll('a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])')]
    .find((el) => el.getClientRects().length && !/open chat/i.test(el.getAttribute('aria-label') || ''));

  // Retry until Richpanel has rendered the target (it builds the panel after the class flips).
  const focusWhenReady = (frame, pick) => {
    let tries = 0;
    const attempt = () => {
      wireDocument(frame);
      const doc = frame.contentDocument;
      const el = doc && pick(doc);
      if (el) { frame.focus(); el.focus(); return; }
      if (++tries < 30) setTimeout(attempt, 100);
    };
    attempt();
  };

  // Richpanel rewrites the frame's document after it opens, which drops its
  // listeners, so the root element carries a mark and wiring is re-checked.
  // The listener sits on the document, not the root: Richpanel stops key
  // events at the document, so a listener lower down never hears Esc.
  const handlers = new WeakMap();
  const wireDocument = (frame) => {
    const doc = frame.contentDocument;
    const root = doc && doc.documentElement;
    if (!root || root.hasAttribute('data-a11y-wired')) return;
    root.setAttribute('data-a11y-wired', '');
    if (handlers.has(doc)) doc.removeEventListener('keydown', handlers.get(doc), true);
    const onKey = (e) => {
      if (isActivation(e) && /open chat/i.test(e.target.getAttribute?.('aria-label') || '')) keyboardAt = Date.now();
      if (e.key === 'Escape' && frame.classList.contains('richpanel-messenger-opened')) {
        const close = findButton(doc, /close chat/i);
        if (!close) return;
        // Stop Richpanel acting on the same key press, and press Close once
        // this event is over: clicking from inside the keydown did nothing.
        e.preventDefault();
        e.stopImmediatePropagation();
        setTimeout(() => {
          close.click();
          focusWhenReady(frame, (d) => findButton(d, /open chat/i));
        }, 0);
      }
    };
    handlers.set(doc, onKey);
    doc.addEventListener('keydown', onKey, true);
  };

  const onFrameChange = (frame) => {
    if (frame.title !== TITLE) frame.title = TITLE;
    wireDocument(frame);
    const opened = frame.classList.contains('richpanel-messenger-opened');
    if (opened && !frame.dataset.a11yOpen) {
      frame.dataset.a11yOpen = '';
      const keepWired = setInterval(() => {
        if (!frame.isConnected || !frame.classList.contains('richpanel-messenger-opened')) clearInterval(keepWired);
        else wireDocument(frame);
      }, 500);
      if (Date.now() - keyboardAt < KEY_WINDOW_MS) focusWhenReady(frame, firstFocusable);
    } else if (!opened) {
      delete frame.dataset.a11yOpen;
    }
  };

  let watched = null;
  const attach = () => {
    const frame = document.getElementById(FRAME_ID);
    if (!frame || frame === watched) return;
    watched = frame;
    new MutationObserver(() => onFrameChange(frame)).observe(frame, { attributes: true, attributeFilter: ['class'] });
    frame.addEventListener('load', () => onFrameChange(frame));
    onFrameChange(frame);
  };

  // The first launcher is in the page, before the frame exists.
  document.addEventListener('keydown', (e) => {
    if (isActivation(e) && e.target.closest?.('.rp-micro-app-dummy-icon')) keyboardAt = Date.now();
  }, true);

  const start = () => {
    attach();
    new MutationObserver(attach).observe(document.body, { childList: true, subtree: true });
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
})();
